import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import styled, {css, withTheme} from 'styled-components';
import progress1 from '../../themes/icons/sho/step-indicator-1.svg';
import progress2 from '../../themes/icons/sho/step-indicator-2.svg';
import progress3 from '../../themes/icons/sho/step-indicator-3.svg';
import progress1of2 from '../../themes/icons/sho/step-indicator-1of2.svg';
import progress2of2 from '../../themes/icons/sho/step-indicator-2of2.svg';
import chevron from '../../themes/icons/azn/chevron.svg';

const Bar = styled.div`
  width: ${props => props.theme.progressBar.width};
  height: ${props => props.theme.progressBar.height || '14px'};

  ${props => props.progress === '1' ? props.theme.progressBar.step1 : null}
  ${props => props.progress === '2' ? props.theme.progressBar.step2 : null}
  ${props => props.progress === '3' ? props.theme.progressBar.step3 : null}
  ${props => props.progress === '1of2' ? props.theme.progressBar.step1of2 : null}
  ${props => props.progress === '2of2' ? props.theme.progressBar.step2of2 : null}
`;

Bar.defaultProps = {
  theme: {
    progressBar: {
      width: '70px',
      step1: css`
        background: url(${progress1}) no-repeat 0;
      `,
      step2: css`
        background: url(${progress2}) no-repeat 0;
      `,
      step3: css`
        background: url(${progress3}) no-repeat 0;
      `,
      step1of2: css`
        background: url(${progress1of2}) no-repeat 0;
      `,
      step2of2: css`
        background: url(${progress2of2}) no-repeat 0;
      `
    }
  }
}

const TabBarContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

// 2. The Individual Step: Handles alignment and the arrow shape
const TabBarStep = styled.div`
  flex: 1; /* Each step takes equal width */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Text Styling */
  font-family: sans-serif;
  font-size: 16px;
  color: ${props => props.$isActive ? props.theme.progressBar.activeColor : props.theme.progressBar.inactiveColor};
  font-weight: ${props => props.$isActive ? 'bold' : 'normal'};

  /* The Chevron Separator (Using a rotated square) */
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    height: ${props => props.theme.progressBar.height || '60px'};
    width: 15px;
    right: -8px;
    ${props => props.theme.progressBar.chevron};
  }
`;

TabBarStep.defaultProps = {
  theme: {
    progressBar: {
      chevron: css`
        background: url(${chevron}) no-repeat 0;
      `
    }
  }
}

const TabBar = ({ progress, formContext = {} }) => {
  let content = (
    <TabBarContainer>
      <TabBarStep $isActive={progress === '1' || progress === 1}>
        {formContext.progressTabLabelStep1 || 'Bedrag'}
      </TabBarStep>

      <TabBarStep $isActive={progress === '2' || progress === 2}>
        {formContext.progressTabLabelStep2 || 'Gegevens'}
      </TabBarStep>

      <TabBarStep $isActive={progress === '3' || progress === 3}>
        {formContext.progressTabLabelStep3 || 'Betaling'}
      </TabBarStep>
    </TabBarContainer>
  );

  if (progress.toString().endsWith('of2')) {
    content = (
      <TabBarContainer>
        <TabBarStep $isActive={progress === '1of2'}>
          {formContext.progressTabLabelStep1 || 'Gegevens'}
        </TabBarStep>

        <TabBarStep $isActive={progress === '2of2'}>
          {formContext.progressTabLabelStep2 || 'Bedankt'}
        </TabBarStep>
      </TabBarContainer>
    );
  }

  return content;
};

const StepBarContainer = styled.div`
  display: flex;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 8px;
  font-family: ${props => props.theme.progressBar.fontFamily};
`;

const Step = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: calc(${props => props.$circleSize} / 2);
    transform: translateY(-50%);
    height: 1px;
    z-index: 0;
  }

  &::before {
    left: 0;
    right: 50%;
    background: ${props => props.$leftGreen
      ? (props.theme.progressBar.lineColorCompleted || '#199A57')
      : (props.theme.progressBar.lineColor || '#e0e0e0')};
  }

  &::after {
    left: 50%;
    right: 0;
    background: ${props => props.$rightGreen
      ? (props.theme.progressBar.lineColorCompleted || '#199A57')
      : (props.theme.progressBar.lineColor || '#e0e0e0')};
  }
`;

const Circle = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.theme.progressBar.circleSize || '28px'};
  height: ${props => props.theme.progressBar.circleSize || '28px'};
  border-radius: 50%;
  font-size: 16px;
  line-height: 1;
  background: ${props => props.$state === 'completed'
    ? (props.theme.progressBar.completedColor || '#199A57')
    : '#fff'};
  border: ${props => props.$state === 'completed'
    ? 'none'
    : props.theme.progressBar.circleBorder};
  color: ${props => props.$state === 'active'
    ? (props.theme.progressBar.activeColor || '#000')
    : (props.theme.progressBar.inactiveColor || '#9b9b9b')};
`;

const Check = styled.span`
  display: inline-block;
  width: 6px;
  height: 11px;
  margin-top: -2px;
  border: solid ${props => props.theme.progressBar.checkColor || '#fff'};
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
`;

const StepLabel = styled.div`
  margin-top: 4px;
  text-align: center;
  white-space: nowrap;
  font-size: ${props => props.theme.progressBar.labelFontSize || '14px'};
  font-weight: ${props => props.theme.progressBar.labelFontWeight};
  line-height: ${props => props.theme.progressBar.labelLineHeight};
  color: ${props => {
    if (props.$state === 'completed') return props.theme.progressBar.completedColor || '#199A57';
    if (props.$state === 'active') return props.theme.progressBar.activeColor || '#000';
    return props.theme.progressBar.inactiveColor || '#9b9b9b';
  }};
`;

const StepBar = ({ progress, theme, formContext = {} }) => {
  const isOf2 = String(progress).endsWith('of2');
  const labels = isOf2
    ? [formContext.progressTabLabelStep1 || 'Gegevens', formContext.progressTabLabelStep2 || 'Bedankt']
    : [
        formContext.progressTabLabelStep1 || 'Keuze',
        formContext.progressTabLabelStep2 || 'Gegevens',
        formContext.progressTabLabelStep3 || 'Doneren'
      ];
  const current = parseInt(progress, 10) || 1;
  const circleSize = theme?.progressBar?.circleSize || '28px';

  return (
    <StepBarContainer>
      {labels.map((label, idx) => {
        const step = idx + 1;
        const state = step < current ? 'completed' : step === current ? 'active' : 'upcoming';
        return (
          <Step
            key={label + step}
            $circleSize={circleSize}
            $leftGreen={step <= current}
            $rightGreen={step < current}
          >
            <Circle $state={state}>
              {state === 'completed' ? <Check /> : step}
            </Circle>
            <StepLabel $state={state}>{label}</StepLabel>
          </Step>
        );
      })}
    </StepBarContainer>
  );
};

class ProgressBar extends Component {
  static propTypes = {
    progress: PropTypes.string.isRequired,
    theme: PropTypes.object.isRequired,
    formContext: PropTypes.object
  };

  render () {
    if (this.props.theme?.progressBar?.useProgressSteps) {
      return <StepBar progress={this.props.progress} theme={this.props.theme} formContext={this.props.formContext} />;
    }
    return this.props.theme?.progressBar?.useProgressTabs ? (
      <TabBar progress={this.props.progress} formContext={this.props.formContext} />
    ) : (
      <Bar progress={this.props.progress} />
    );
  }
}

const mapStateToProps = state => ({
  formContext: state.reducer.formContext
});

export default connect(mapStateToProps)(withTheme(ProgressBar));
